import { useState } from 'react';
import { Link } from 'react-router-dom';

interface AgentAvatarProps {
  agentId: string;
  agentName: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: React.CSSProperties;
  clickable?: boolean; // Whether to link to agent profile (default: true)
}

/**
 * Extract meaningful initials from an agent name
 */
function getInitials(name: string): string {
  const cleaned = name.trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  
  if (parts.length === 0) {
    return '??';
  }
  
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Generate a consistent color based on the agent name
 */
function getColorHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash * 137.508) % 360;
}

const sizeClasses = {
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
};

/**
 * AgentAvatar - Displays an avatar with initials and hover tooltip
 * Clicking navigates to the agent's profile page
 */
const AgentAvatar: React.FC<AgentAvatarProps> = ({ 
  agentId,
  agentName,
  size = 'md',
  className = '',
  style,
  clickable = true,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const initials = getInitials(agentName);
  const hue = getColorHue(agentName);
  
  const bgColor = `hsl(${hue}, 60%, 25%)`;
  const borderColor = `hsl(${hue}, 70%, 40%)`;
  const textColor = `hsl(${hue}, 80%, 80%)`;

  const avatarElement = (
    <div 
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-bold border-2 select-none transition-transform hover:scale-110 ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
      style={{ 
        backgroundColor: bgColor,
        borderColor: borderColor,
        color: textColor,
      }}
      title={agentName}
    >
      {initials}
    </div>
  );

  const wrapperContent = (
    <>
      {avatarElement}
      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-background border border-border rounded text-xs text-foreground whitespace-nowrap z-50 shadow-lg">
          {agentName}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-border" />
        </div>
      )}
    </>
  );

  if (clickable) {
    return (
      <Link 
        to={`/agents/${agentId}`}
        className={`relative inline-block ${className}`}
        style={style}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {wrapperContent}
      </Link>
    );
  }

  return (
    <div 
      className={`relative inline-block ${className}`}
      style={style}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {wrapperContent}
    </div>
  );
};

export default AgentAvatar;
